import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tv, Smartphone, Cast, ArrowLeft, Copy, Check, Play } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

/**
 * TVGuidePage — visual instructions for adding TimeCapsule 24 to any TV.
 * Reachable at /tv. Doubles as a shareable landing page for marketing.
 */
export default function TVGuidePage() {
  const url = typeof window !== "undefined" ? window.location.origin : "";
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = "Watch TimeCapsule 24 on your TV · Setup guide";
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked */ }
  };

  const platforms = [
    {
      key: "samsung",
      name: "Samsung Smart TV",
      icon: "📺",
      years: "2021 or newer (Tizen 6+)",
      steps: [
        "Open the built-in browser on your TV (usually 'Internet' or 'Smart Hub')",
        `Type: ${url.replace(/^https?:\/\//, "")}`,
        "Once loaded, press the ☰ menu button → 'Add to Home' or 'Bookmark'",
        "TimeCapsule now lives on your Samsung home rail — one click to launch",
      ],
    },
    {
      key: "lg",
      name: "LG OLED / webOS",
      icon: "📺",
      years: "2021 or newer (webOS 6+)",
      steps: [
        "Open the LG Web Browser from your home screen",
        `Enter: ${url.replace(/^https?:\/\//, "")}`,
        "Tap ⋮ (three dots) → 'Add to Home Screen'",
        "Confirm — the TimeCapsule icon appears on your LG home",
      ],
    },
    {
      key: "chromecast",
      name: "Chromecast / Google TV",
      icon: "📡",
      years: "Any Chromecast device",
      steps: [
        "On your phone or laptop, open TimeCapsule 24 in Chrome",
        "Tap the ⋮ menu → 'Cast'",
        "Pick your Chromecast device from the list",
        "Cinema Mode plays fullscreen on your TV — phone becomes the remote",
      ],
    },
    {
      key: "apple",
      name: "Apple TV / AirPlay",
      icon: "🍏",
      years: "Apple TV HD or newer",
      steps: [
        "On your iPhone or Mac, open TimeCapsule 24 in Safari",
        "Tap the AirPlay icon (or Screen Mirror on iPhone)",
        "Select your Apple TV from the list",
        "The screen mirrors — Cinema Mode fills your TV",
      ],
    },
    {
      key: "firetv",
      name: "Amazon Fire TV",
      icon: "🔥",
      years: "Any Fire TV device",
      steps: [
        "From the home screen, open the 'Silk Browser' (search 'Silk' in apps)",
        `Type: ${url.replace(/^https?:\/\//, "")}`,
        "Bookmark it — the bookmark appears on your Fire TV home rail",
      ],
    },
    {
      key: "roku",
      name: "Roku (via mobile mirror)",
      icon: "📱",
      years: "Any Roku",
      steps: [
        "Enable Screen Mirror on Roku: Settings → System → Screen mirroring → Enable",
        "On your Android phone/tablet: Cast/Smart View → pick your Roku",
        "Open TimeCapsule 24 in a fullscreen browser — the whole screen mirrors to Roku",
        "iPhone users: use AirPlay to an Apple TV instead (Roku doesn't do AirPlay natively)",
      ],
    },
  ];

  return (
    <div className="min-h-screen paper-grain" data-testid="tv-guide-page">
      <div className="border-b-2 border-ink px-6 sm:px-10 py-4 flex items-center gap-4 bg-paper-alt">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-mono uppercase tracking-[0.3em] text-[10px] sm:text-xs hover:text-press-red"
          data-testid="tv-guide-back"
        >
          <ArrowLeft size={12} /> Back
        </Link>
      </div>

      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-10 sm:py-14">
        <div className="font-mono uppercase tracking-[0.35em] text-[10px] sm:text-xs text-press-red mb-3 flex items-center gap-2">
          <Tv size={14} /> Watch on your TV
        </div>
        <h1
          className="font-headline uppercase font-black leading-[0.95] tracking-tighter text-3xl sm:text-5xl lg:text-6xl"
          data-testid="tv-guide-h1"
        >
          TimeCapsule 24 on the big screen.
        </h1>
        <p className="mt-4 font-body italic text-base sm:text-lg text-ink-muted max-w-2xl">
          No app store, no download. Any Smart TV made in the last 5 years can pull up TimeCapsule from its built-in browser — or cast from your phone in one tap.
        </p>

        {/* Quick QR box */}
        <div className="mt-8 border-2 border-ink bg-paper-alt p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-6">
          <div className="p-3 bg-white border-2 border-ink flex-shrink-0" data-testid="tv-guide-qr">
            <QRCodeSVG value={url} size={140} level="M" includeMargin={false} />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="font-headline uppercase font-bold text-lg sm:text-xl leading-tight">
              Fast track: scan from your TV
            </div>
            <p className="mt-1 font-body italic text-sm text-ink-muted">
              Point your TV's camera app or a phone at this QR code to open TimeCapsule on that device.
            </p>
            <div className="mt-3 flex items-center gap-2 flex-wrap justify-center sm:justify-start">
              <code className="font-mono text-xs sm:text-sm bg-paper px-2 py-1 border border-ink">{url.replace(/^https?:\/\//, "")}</code>
              <button
                onClick={copy}
                className="inline-flex items-center gap-1.5 font-mono uppercase tracking-widest text-[10px] border-2 border-ink px-2.5 py-1 hover:bg-ink hover:text-paper"
                data-testid="tv-guide-copy-btn"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        </div>

        {/* Ambient TV mode — screensaver-style loop */}
        <div
          className="mt-6 border-2 border-ink bg-ink text-paper p-5 sm:p-7 shadow-hard flex flex-col sm:flex-row items-center gap-4 sm:gap-6"
          data-testid="tv-guide-ambient-card"
        >
          <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 border-2 border-paper/40 flex items-center justify-center bg-press-red">
            <Play size={26} />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="font-mono uppercase tracking-[0.3em] text-[10px] text-paper/60 mb-1">
              ✦ Try it now — no setup
            </div>
            <div className="font-headline uppercase font-black text-xl sm:text-2xl leading-tight tracking-tight">
              Ambient TV Mode
            </div>
            <p className="mt-1 font-body italic text-sm text-paper/70">
              A silent looping montage of five iconic days — perfect for putting on a TV or iPad in the background. Tap any moment to jump into full Cinema Mode.
            </p>
          </div>
          <Link
            to="/tv/ambient"
            className="inline-flex items-center gap-2 bg-paper text-ink border-2 border-paper px-5 py-3 font-mono uppercase tracking-widest text-xs sm:text-sm hover:bg-press-red hover:border-press-red hover:text-paper whitespace-nowrap"
            data-testid="tv-guide-ambient-btn"
          >
            <Play size={14} /> Start Ambient Mode →
          </Link>
        </div>

        {/* Cast Receiver — pair phone with TV */}
        <div
          className="mt-6 border-2 border-ink bg-paper-alt p-5 sm:p-7 shadow-hard flex flex-col sm:flex-row items-center gap-4 sm:gap-6"
          data-testid="tv-guide-receiver-card"
        >
          <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 border-2 border-ink flex items-center justify-center bg-ink text-paper">
            <Tv size={26} />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="font-mono uppercase tracking-[0.3em] text-[10px] text-ink-muted mb-1">
              ✦ Turn your phone into a remote
            </div>
            <div className="font-headline uppercase font-black text-xl sm:text-2xl leading-tight tracking-tight">
              Cast from your phone
            </div>
            <p className="mt-1 font-body italic text-sm text-ink-muted">
              Open Cast Receiver on this TV → your phone types the code once → every date you tap plays instantly on the TV.
            </p>
          </div>
          <Link
            to="/tv/cast"
            className="inline-flex items-center gap-2 bg-ink text-paper border-2 border-ink px-5 py-3 font-mono uppercase tracking-widest text-xs sm:text-sm hover:bg-press-red hover:border-press-red hover:text-paper whitespace-nowrap"
            data-testid="tv-guide-receiver-btn"
          >
            <Tv size={14} /> Open Cast Receiver →
          </Link>
        </div>

        {/* Platform cards */}
        <section className="mt-12 space-y-6">
          {platforms.map((p, i) => (
            <div key={p.key} className="border-2 border-ink shadow-hard-sm bg-paper-surface" data-testid={`tv-guide-platform-${p.key}`}>
              <div className="border-b-2 border-ink px-5 py-3 flex items-center gap-3 bg-paper-alt">
                <span className="text-2xl" aria-hidden>{p.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-headline uppercase font-black text-lg sm:text-xl leading-tight tracking-tight">
                    {p.name}
                  </div>
                  <div className="font-mono uppercase tracking-widest text-[10px] text-ink-muted mt-0.5">
                    {p.years}
                  </div>
                </div>
                <div className="hidden sm:block font-mono uppercase tracking-widest text-[10px] text-press-red">
                  {String(i + 1).padStart(2, "0")}
                </div>
              </div>
              <ol className="p-5 space-y-2 font-body text-sm sm:text-base list-decimal list-inside">
                {p.steps.map((s, si) => (
                  <li key={si} className="leading-relaxed">{s}</li>
                ))}
              </ol>
            </div>
          ))}
        </section>

        {/* Bottom CTA */}
        <section className="mt-12 border-2 border-ink bg-paper-alt p-6 sm:p-10 text-center">
          <div className="font-mono uppercase tracking-[0.3em] text-[10px] text-press-red mb-3 flex items-center justify-center gap-2">
            <Cast size={12} /> Any device, one tap
          </div>
          <div className="font-headline uppercase font-black text-2xl sm:text-4xl tracking-tighter">
            Cinema Mode. Full-screen. Zero apps.
          </div>
          <p className="mt-3 font-body italic text-ink-muted max-w-lg mx-auto">
            Add TimeCapsule to your TV home screen and it launches like a native app. First install takes 60 seconds.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 bg-press-red text-paper border-2 border-press-red px-6 py-3 font-mono uppercase text-xs sm:text-sm tracking-widest hover:bg-ink hover:border-ink"
            data-testid="tv-guide-home-cta"
          >
            <Smartphone size={14} /> Open TimeCapsule 24 →
          </Link>
        </section>
      </div>

      <footer className="border-t-2 border-ink px-6 sm:px-10 py-6 mt-10 text-center bg-paper-alt">
        <div className="font-mono uppercase tracking-[0.3em] text-[10px] text-ink-muted">
          TimeCapsule 24 · A daily snapshot machine for the last century.
        </div>
        <div className="mt-2 flex items-center justify-center gap-3 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
          <Link to="/contact" className="hover:text-press-red" data-testid="footer-contact-link">Contact</Link>
          <span aria-hidden="true">·</span>
          <a href="mailto:support@timecapsule24.tv" className="hover:text-press-red">support@timecapsule24.tv</a>
        </div>
      </footer>
    </div>
  );
}
